import { useEffect, useState } from "react";
import { Rows3 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

function rotP(p, deg, o) {
  const a = (deg * Math.PI) / 180, dx = p.x - o.x, dy = p.y - o.y;
  return { x: o.x + dx * Math.cos(a) - dy * Math.sin(a), y: o.y + dx * Math.sin(a) + dy * Math.cos(a) };
}
export function rectPts(cx, cy, w, h, angDeg) {
  const a = (angDeg * Math.PI) / 180, hw = w / 2, hh = h / 2;
  return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([dx, dy]) => ({
    x: cx + dx * Math.cos(a) - dy * Math.sin(a), y: cy + dx * Math.sin(a) + dy * Math.cos(a),
  }));
}

// Ruota i pannelli selezionati come BLOCCO attorno al loro baricentro.
export function rotateGroup(panels, sel, deg) {
  const ps = panels.filter((p) => sel.has(p.id));
  if (!ps.length) return panels;
  const o = { x: ps.reduce((s, p) => s + p.cx, 0) / ps.length, y: ps.reduce((s, p) => s + p.cy, 0) / ps.length };
  return panels.map((p) => {
    if (!sel.has(p.id)) return p;
    const c = rotP({ x: p.cx, y: p.cy }, deg, o);
    return { ...p, cx: c.x, cy: c.y, ang: p.ang + deg };
  });
}

// "Aggiungi stringa": blocco file × pannelli che segue il mouse; click = posa, Esc = fine.
export function usePanelString({ pw, ph, gap, rowPitch, angAt, onPlace }) {
  const [dlg, setDlg] = useState(false);
  const [cfg, setCfg] = useState({ orient: "verticale", rows: 1, cols: 10 });
  const [active, setActive] = useState(false);
  const [cur, setCur] = useState(null);
  const [extra, setExtra] = useState(0);
  const short = Math.min(pw, ph), long = Math.max(pw, ph);
  const w = cfg.orient === "verticale" ? short : long, h = cfg.orient === "verticale" ? long : short;
  useEffect(() => {
    if (!active) return undefined;
    const k = (e) => {
      if (e.target?.tagName === "INPUT") return;
      if (e.key === "Escape") { setActive(false); setCur(null); }
      else if (e.key === "r" || e.key === "R") setExtra((x) => x + 90);
      else if (e.key === "e" || e.key === "E") setExtra((x) => x + 15);
      else if (e.key === "q" || e.key === "Q") setExtra((x) => x - 15);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [active]);
  const block = () => {
    if (!cur) return [];
    const ang = (angAt(cur) || 0) + extra;
    const px = w + gap, py = cfg.orient === "verticale" || !rowPitch ? h + gap : Math.max(h + gap, rowPitch);
    const rows = Math.max(1, cfg.rows | 0), cols = Math.max(1, cfg.cols | 0);
    const out = [];
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      const loc = { x: cur.x + (c - (cols - 1) / 2) * px, y: cur.y + (r - (rows - 1) / 2) * (rowPitch || py) };
      const p = rotP(loc, ang, cur);
      out.push({ cx: p.x, cy: p.y, ang, w, h });
    }
    return out;
  };
  return {
    dlg, setDlg, cfg, setCfg, active, extra,
    start: () => { setDlg(false); setExtra(0); setActive(true); },
    stop: () => { setActive(false); setCur(null); },
    move: (m) => { if (active && m) setCur(m); },
    place: () => { const b = block(); if (b.length) onPlace(b); },
    preview: block,
  };
}

export function StringButton({ s, prefix }) {
  return (
    <Button size="sm" variant={s.active ? "default" : "outline"} onClick={() => (s.active ? s.stop() : s.setDlg(true))} data-testid={`${prefix}-string-btn`}>
      <Rows3 className="h-4 w-4 mr-1" strokeWidth={1.5} />{s.active ? "Fine stringa (Esc)" : "Aggiungi stringa"}
    </Button>
  );
}

export function StringDialog({ s, wp, prefix }) {
  const n = Math.max(1, s.cfg.rows | 0) * Math.max(1, s.cfg.cols | 0);
  const set = (k, v) => s.setCfg((c) => ({ ...c, [k]: v }));
  return (
    <Dialog open={s.dlg} onOpenChange={s.setDlg}>
      <DialogContent className="max-w-sm" data-testid={`${prefix}-string-dialog`}>
        <DialogHeader>
          <DialogTitle>Aggiungi stringa di pannelli</DialogTitle>
          <DialogDescription>Dopo OK il blocco segue il mouse: clic per posarlo (anche più volte), R ruota 90°, Q/E ±15°, Esc per finire.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {[["verticale", "Verticale"], ["orizzontale", "Orizzontale"]].map(([k, l]) => (
              <Button key={k} size="sm" className="flex-1" variant={s.cfg.orient === k ? "default" : "outline"} onClick={() => set("orient", k)} data-testid={`${prefix}-string-orient-${k}`}>{l}</Button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs">Numero di file<Input type="number" min="1" max="50" value={s.cfg.rows} onChange={(e) => set("rows", e.target.value)} data-testid={`${prefix}-string-rows`} /></label>
            <label className="text-xs">Pannelli per fila<Input type="number" min="1" max="200" value={s.cfg.cols} onChange={(e) => set("cols", e.target.value)} data-testid={`${prefix}-string-cols`} /></label>
          </div>
          <p className="text-sm font-semibold" data-testid={`${prefix}-string-total`}>{n} pannelli{wp ? ` · ${((n * wp) / 1000).toFixed(2)} kWp` : ""}</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => s.setDlg(false)}>Annulla</Button>
          <Button onClick={s.start} data-testid={`${prefix}-string-ok`}>OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function StringPreview({ s, P }) {
  if (!s.active) return null;
  return (
    <g pointerEvents="none" data-testid="string-preview">
      {s.preview().map((p, i) => {
        const pts = rectPts(p.cx, p.cy, p.w, p.h, p.ang).map((m) => { const q = P(m); return `${q.x.toFixed(1)},${q.y.toFixed(1)}`; }).join(" ");
        return <polygon key={i} points={pts} fill="#FDD00366" stroke="#FDD003" strokeWidth="1.2" strokeDasharray="4 2" />;
      })}
    </g>
  );
}

export function GroupRotate({ disabled, onRotate, prefix }) {
  const [deg, setDeg] = useState("");
  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" onClick={() => onRotate(-15)} disabled={disabled} data-testid={`${prefix}-rotate-m15`}>−15°</Button>
      <Button size="sm" variant="outline" onClick={() => onRotate(15)} disabled={disabled} data-testid={`${prefix}-rotate15`}>+15°</Button>
      <Button size="sm" variant="outline" onClick={() => onRotate(90)} disabled={disabled} data-testid={`${prefix}-rotate90`}>90°</Button>
      <Input value={deg} onChange={(e) => setDeg(e.target.value)} placeholder="°" className="h-8 w-14" disabled={disabled} data-testid={`${prefix}-rotate-input`} />
      <Button size="sm" variant="outline" disabled={disabled || !Number(deg)} onClick={() => onRotate(Number(deg))} data-testid={`${prefix}-rotate-apply`}>Ruota</Button>
    </div>
  );
}
